import { Column, Table } from "react-migration/components/TableDeprecated";
import { LetterEventName, logLetterEvent } from "../../letters/helpers/logLetterEvent";
import React, { useEffect } from "react";
import { SingleRecipient, singleRecipientValidationSchema } from "./SingleRecipientValidationSchema";

import Feature from "src/js/stores/user/Feature";
import { SelectRecipientsFormCell } from "./SelectRecipientsFormCell";
import { SelectRecipientsFormData } from "./SelectRecipientsFormSchema";
import { TableTitle } from "./TableTitle";
import { Theme } from "react-migration/lib/theme/Theme";
import { Tooltip } from "react-migration/components/Tooltip";
import classNames from "clsx";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { letterStore } from "src/js/stores/letter/store";
import { updateSelectedRecipientIds } from "src/js/stores/letter/actions";
import { useFormContext } from "react-hook-form";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export interface SelectRecipientsFormProps extends React.PropsWithChildren {
  onSubmit?: (formData: SelectRecipientsFormData) => void;
}

export const SELECT_RECIPIENTS_FORM_ID = "selectRecipientsForm";

export const SelectRecipientsForm = ({ onSubmit, children }: SelectRecipientsFormProps) => {
  const { t } = useTranslation();
  const usAccess = hasFeature(Feature.usAccess);
  const { selectedRecipientIds } = useSnapshot(letterStore);

  const methods = useFormContext<SelectRecipientsFormData>();

  const { handleSubmit, trigger, getValues, setValue } = methods;

  const { recipients } = getValues();

  useEffect(() => {
    if (recipients.length > 0) {
      updateSelectedRecipientIds(
        recipients.filter((recipient) => recipient.isGranted).map((recipient) => recipient._id)
      );
      trigger();
    }
  }, [trigger, recipients]);

  const onFormSubmit = handleSubmit((formData) => {
    onSubmit?.(formData);
  });

  const PropertyAddressHeader = () => (
    <div className="flex items-center gap-1">
      {t("sites.letters.property_address")}
      <Tooltip.Wrapper>
        <Tooltip.Trigger>
          <i className="icon-lt-information-line text-lg cursor-pointer text-text-base-tertiary" aria-label="Info" />
        </Tooltip.Trigger>
        <Tooltip.Content align="center" side="bottom" theme={Theme.Dark} size="sm">
          <span className="whitespace-normal">{t("sites.letters.property_address_tooltip")}</span>
        </Tooltip.Content>
      </Tooltip.Wrapper>
    </div>
  );

  const columns: Column<SingleRecipient>[] = [
    {
      id: "name",
      label: t("sites.letters.name"),
      grow: true,
      render: (recipient, rowIndex: number) => (
        <SelectRecipientsFormCell recipient={recipient} rowIndex={rowIndex} displayingProperty="fullName" />
      ),
    },
    {
      id: "addressLine1",
      label: t("sites.letters.address_line_1"),
      grow: true,
      render: (recipient, rowIndex: number) => (
        <SelectRecipientsFormCell recipient={recipient} rowIndex={rowIndex} displayingProperty="addressLine1" />
      ),
    },
    {
      id: "city",
      label: t("sites.letters.city"),
      grow: true,
      render: (recipient, rowIndex: number) => (
        <SelectRecipientsFormCell recipient={recipient} rowIndex={rowIndex} displayingProperty="city" />
      ),
    },
    {
      id: "region",
      label: t("sites.letters.region"),
      grow: true,
      render: (recipient, rowIndex: number) => (
        <SelectRecipientsFormCell recipient={recipient} rowIndex={rowIndex} displayingProperty="region" />
      ),
    },
    {
      id: "postcode",
      label: t("sites.letters.post_code"),
      grow: true,
      render: (recipient, rowIndex: number) => (
        <SelectRecipientsFormCell recipient={recipient} rowIndex={rowIndex} displayingProperty="postCode" />
      ),
    },
    {
      id: "propertyAddress",
      label: <PropertyAddressHeader />,
      maxWidth: "400px",
      grow: true,
      render: (recipient, rowIndex: number) => (
        <SelectRecipientsFormCell recipient={recipient} rowIndex={rowIndex} displayingProperty="propertyAddress" />
      ),
    },
    {
      label: t("sites.letters.site_name"),
      rowKey: "siteTitle",
      maxWidth: "400px",
    },
  ];

  if (!usAccess) {
    columns.splice(5, 0, {
      label: t("sites.letters.title"),
      rowKey: "title",
    });
  }

  const isSelectionDisabled = (recipient?: SingleRecipient) =>
    (recipient?.hasOwnProperty("isGranted") && !recipient.isGranted) ?? false;

  const invalidRowClassName = (recipient?: SingleRecipient) => {
    const isValid = singleRecipientValidationSchema.safeParse(recipient).success;
    if (!isValid && recipient?.isSelected) {
      return "bg-negative-500/10 hover:bg-negative-500/20!";
    }
  };

  const cellClassName = (_r: SingleRecipient, { id }: Column<SingleRecipient>) => {
    if (id) {
      return "p-0!";
    }
  };

  const headerCellClassName = (column: Column<SingleRecipient>) => {
    if (column.id === "propertyAddress") {
      return "min-w-[300px]";
    }
  };

  return (
    <div className={classNames("bg-background-surface-neutral", "p-6", "h-full", "w-full", "grid", "overflow-x-auto")}>
      <form
        onSubmit={onFormSubmit}
        id={SELECT_RECIPIENTS_FORM_ID}
        data-testid="select-recipients-form"
        autoComplete="off"
      >
        <Table
          titleHeader={<TableTitle recipients={recipients} />}
          titleHeaderClassNames="w-full"
          primaryKey={"_id"}
          rows={recipients ?? []}
          columns={columns}
          selection={{
            selectedRowIds: selectedRecipientIds ?? [],
            updateSelectedRowIds: updateSelectedRecipientIds,
            selectWithRowClick: false,
            onSelectAllRows: () => {
              recipients.forEach((row) => {
                const recipientIndex = recipients.findIndex((recipient) => recipient._id === row._id);

                recipients[recipientIndex].isGranted &&
                  setValue(`recipients.${recipientIndex}.isSelected`, !selectedRecipientIds!.includes(row._id), {
                    shouldValidate: true,
                  });
              });

              logLetterEvent({
                name: LetterEventName.LETTERS_FORM_SELECT_RECIPIENTS_STEP_ALL_RECIPIENTS_TOGGLED,
              });
            },
            onSelectRow: (row) => {
              const recipientIndex = recipients.findIndex((recipient) => recipient._id === row._id);

              setValue(`recipients.${recipientIndex}.isSelected`, !selectedRecipientIds!.includes(row._id), {
                shouldValidate: true,
              });

              trigger();

              logLetterEvent({
                name: LetterEventName.LETTERS_FORM_SELECT_RECIPIENTS_STEP_RECIPIENT_TOGGLED,
              });
            },
            disabled: isSelectionDisabled,
          }}
          rowClassName={invalidRowClassName}
          cellClassName={cellClassName}
          headerCellClassName={headerCellClassName}
        />
        {children}
      </form>
    </div>
  );
};
